/** Formatting helpers shared across the Statement design. */

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const monthName = (i: number): string => MONTHS[((i % 12) + 12) % 12];
export const monthShort = (i: number): string => MONTHS_SHORT[((i % 12) + 12) % 12];

/** `$1,234` — whole dollars, the default for headline figures and chart labels. */
export function money(v: number | null | undefined): string {
  const n = Number(v ?? 0);
  const sign = n < 0 ? '−' : '';
  return `${sign}$${Math.round(Math.abs(n)).toLocaleString('en-US')}`;
}

/** `$1,234.56` — used where cents matter (transaction rows, balances). */
export function money2(v: number | null | undefined): string {
  const n = Number(v ?? 0);
  const sign = n < 0 ? '−' : '';
  return `${sign}$${Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Symbols for the currencies an account is likely to be held in. */
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$', EUR: '€', GBP: '£', JPY: '¥', CHF: 'CHF ', CAD: 'C$', AUD: 'A$',
  CNY: '¥', HKD: 'HK$', SEK: 'kr ', NOK: 'kr ', DKK: 'kr ', PLN: 'zł ',
  NZD: 'NZ$', SGD: 'S$', INR: '₹', BRL: 'R$', MXN: 'MX$', ZAR: 'R ',
};

/**
 * `€3,870` — an amount in its own currency, whole units.
 *
 * Used for the local half of a converted balance, where the point is to show
 * what the bank itself says rather than to be precise to the cent.
 */
export function localMoney(v: number | null | undefined, currency: string): string {
  const n = Number(v ?? 0);
  const code = (currency || 'USD').toUpperCase();
  const symbol = CURRENCY_SYMBOLS[code] ?? `${code} `;
  const sign = n < 0 ? '−' : '';
  return `${sign}${symbol}${Math.round(Math.abs(n)).toLocaleString('en-US')}`;
}

/** `+$3,654` / `−$1,607` — a signed whole-dollar delta. */
export function signedMoney(v: number | null | undefined): string {
  const n = Number(v ?? 0);
  return n >= 0 ? `+${money(n)}` : money(n);
}

export function percent(v: number | null | undefined, digits = 1): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${v.toFixed(digits)}%`;
}

export function signedPercent(v: number | null | undefined, digits = 0): string {
  if (v == null || !Number.isFinite(v)) return '—';
  // `>= 0`, matching signedMoney: a holding that has not moved reads "+0.00%",
  // not "−0.00%", which says the wrong thing about a number that is zero.
  return `${v >= 0 ? '+' : '−'}${Math.abs(v).toFixed(digits)}%`;
}

/** ISO `YYYY-MM-DD` in local time (avoids the UTC shift `toISOString` introduces). */
export function isoDate(d: Date): string {
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/**
 * Names the heatmap's active selection: `August 14`, or `August 2025` when a
 * whole month was picked. Lives here because the chip that shows it sits in the
 * shell bar while the filter it names belongs to Transactions.
 */
export function dayFilterLabel(day: string): string {
  const d = parseDate(day.length === 7 ? `${day}-01` : day);
  if (!d) return day;
  return day.length === 7
    ? `${monthName(d.getMonth())} ${d.getFullYear()}`
    : `${monthName(d.getMonth())} ${d.getDate()}`;
}

/** Parses `YYYY-MM-DD` (or a full ISO timestamp) as a local-time date. */
export function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const s = String(value);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** `Friday, July 31` — the day heading used in the transactions list. */
export function dayHeading(d: Date): string {
  return `${WEEKDAYS[d.getDay()]}, ${monthName(d.getMonth())} ${d.getDate()}`;
}

/** `Jul 31` — compact date for dense rows. */
export function shortDay(d: Date): string {
  return `${monthShort(d.getMonth())} ${d.getDate()}`;
}

/**
 * A `YYYY-MM-DD` as `Sep 20, 2024`, or back as it came if it will not parse.
 *
 * The year matters wherever the date is evidence rather than context — when a
 * lot was bought, how far an institution's history reaches — because "Sep 20"
 * two years ago and "Sep 20" this year are different facts.
 */
export function dateWithYear(day: string | null | undefined): string {
  const d = parseDate(day ?? null);
  return d ? `${shortDay(d)}, ${d.getFullYear()}` : (day ?? '');
}

/** Human "time ago" for sync timestamps. */
export function timeAgo(value: string | null | undefined): string {
  // A full timestamp is read as the instant it names. `parseDate` keeps only
  // the calendar day (local midnight), which turned "two hours ago" into
  // "just now" whenever the UTC date had already rolled over locally.
  const exact = value && value.includes('T') ? new Date(value) : null;
  const d = exact && !Number.isNaN(exact.getTime()) ? exact : parseDate(value ?? null);
  if (!d) return 'never';
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
  return `${Math.floor(months / 12)} year${months < 24 ? '' : 's'} ago`;
}

/**
 * Institution swatches, ordered for maximum separation between neighbours.
 * Assigned by position in a sorted list rather than by hash — hashing collides
 * (Chase and Capital One landed on the same blue), and a user with a handful of
 * banks should never see the same colour twice.
 */
export const INSTITUTION_PALETTE = [
  '#1B39D6', // blue
  '#FF5A3C', // orange-red
  '#12A594', // teal
  '#E0A400', // amber
  '#6E4BD8', // purple
  '#D6409F', // magenta
  '#0EA5E9', // sky
  '#16A34A', // green
  '#E11D48', // rose
  '#A16207', // brown
  '#C026D3', // fuchsia
  '#475569', // slate
];

/**
 * Builds a name → colour map with no duplicates. Sorting first keeps a given
 * institution on the same colour across pages and reloads.
 */
export function buildColorMap(names: Array<string | null | undefined>): Map<string, string> {
  const unique = Array.from(
    new Set(names.map((n) => (n ?? '').trim()).filter((n) => n.length > 0)),
  ).sort((a, b) => a.localeCompare(b));
  const map = new Map<string, string>();
  unique.forEach((name, i) => map.set(name, INSTITUTION_PALETTE[i % INSTITUTION_PALETTE.length]));
  return map;
}

/** Deterministic fallback for a name that isn't in the current map. */
export function colorFor(key: string | null | undefined, offset = 0): string {
  const s = String(key ?? '');
  if (!s) return 'var(--st-ink5)';
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  return INSTITUTION_PALETTE[(hash + offset) % INSTITUTION_PALETTE.length];
}

/**
 * One colour per category, from the design handoff. Two categories may share a
 * hue (Bank Fees and Utilities are both slate; Rent & Mortgage and Debt
 * Payments are both violet) — the icon is what tells them apart, and the
 * palette is grouped so a leaf reads as income, spending or transfer before you
 * have read its name.
 *
 * Stable across periods so a category keeps its colour as the data changes.
 * `Reimbursements` is not in the handoff's taxonomy and deliberately mirrors
 * Refunds & Rebates, which is the same shape of thing.
 */
const CATEGORY_COLORS: Record<string, string> = {
  // Spending
  'bank fees': '#7A8B99',
  'debt payments': '#6E4BD8',
  dining: '#B4531F',
  entertainment: '#4E6136',
  groceries: '#5C6F86',
  healthcare: '#E0557B',
  'other expense': '#9A9689',
  'personal care': '#8A6A3B',
  reimbursements: '#3FA97A',
  'rent & mortgage': '#6B7A8F',
  shopping: '#A08A2E',
  taxes: '#8A7A5C',
  transportation: '#8A6A3B',
  travel: '#9E4719',
  utilities: '#8B8375',
  // Income
  'benefits & pensions': '#4FA48C',
  'investment income': '#0E8C7F',
  'other income': '#12A594',
  'refunds & rebates': '#3FA97A',
  'rental income': '#2F9E8F',
  'salary & wages': '#12A594',
  // Transfer. Cash & ATM is spending in our taxonomy but keeps the handoff's
  // colour, which is assigned per category rather than per parent.
  'cash & atm': '#5B7A9E',
  'credit card payments': '#6E7FA8',
  'internal transfers': '#7A8B99',
  'investments & retirement': '#4E6E8C',
  'loan proceeds': '#8A93A8',
  'other transfers': '#93A0AC',
  // Aliases for names stored before the taxonomy split, so historical rows and
  // any cached API response keep their colour.
  income: '#12A594',
  rent: '#6B7A8F',
  housing: '#6B7A8F',
  other: '#9A9689',
  'loans & transfers': '#93A0AC',
  'gifts & donations': '#9A9689',
  'food and drink': '#B4531F',
  restaurants: '#B4531F',
};

/** Fallback hues for categories outside the canonical set, chosen to sit in the
 *  gaps between the assigned ones. */
const EXTRA_COLORS = ['#0891B2', '#B45309', '#4F46E5', '#DB2777', '#059669', '#9333EA'];

export function categoryColor(name: string | null | undefined): string {
  const key = (name ?? '').trim().toLowerCase();
  if (!key) return 'var(--st-ink5)';
  const fixed = CATEGORY_COLORS[key];
  if (fixed) return fixed;
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return EXTRA_COLORS[hash % EXTRA_COLORS.length];
}
